import { lazy, Suspense } from "react";

import { lazyWithSuspense } from "#routes/lazyWithSuspense";
import { createRouteComponent } from "#routes/createRouteComponent";

import { NAVIGATION_ROUTES } from "#routes/routes.constant";
import type { AppRoute } from "#routes/types";

/* --------------------------------------------------------------------- */
/*  All page components are lazy-loaded – keep the import path short   */
/* --------------------------------------------------------------------- */
function preloadable<T>(loader: () => Promise<T>) {
  let promise: Promise<T> | undefined;
  return () => {
    if (!promise) {
      promise = loader();
    }
    return promise;
  };
}

const loadDashboard = preloadable(() => import("#views/Dashboard"));
const loadIncidents = preloadable(() => import("#views/Incidents"));
const loadLossAndDamage = preloadable(() => import("#views/LossAndDamage"));
const loadRealTime = preloadable(() => import("#views/RealTimeMonitoring"));
const loadProfile = preloadable(() => import("#views/Profile"));
const loadRiskInfo = preloadable(() => import("#views/RiskInfo"));
const loadDataArchive = preloadable(() => import("#views/DataArchive"));
const loadVizRisk = preloadable(() => import("#views/VizRisk"));
const loadIbf = preloadable(() => import("#views/IBF"));

const routePreloaders: Record<string, () => Promise<unknown>> = {
  dashboard: loadDashboard,
  incident: loadIncidents,
  lossAndDamage: loadLossAndDamage,
  realtime: loadRealTime,
  profile: loadProfile,
  riskInfo: loadRiskInfo,
  dataArchive: loadDataArchive,
  visrisk: loadVizRisk,
  ibf: loadIbf,
};

export const NAVBAR_ROUTE_PRELOAD_ORDER = [
  "profile",
  "incident",
  "realtime",
  "riskInfo",
  "dataArchive",
  "lossAndDamage",
  "dashboard",
  "ibf",
  "visrisk",
];

export const preloadRouteByName = (routeName: string | undefined) => {
  if (!routeName) {
    return;
  }
  void routePreloaders[routeName]?.();
};

const LazyDashboard = lazyWithSuspense(loadDashboard);
const LazyIncidents = lazyWithSuspense(loadIncidents);
const LazyLossAndDamage = lazyWithSuspense(loadLossAndDamage);
const LazyRealTime = lazyWithSuspense(loadRealTime);
const LazyProfile = lazyWithSuspense(loadProfile);
const LazyProfileDemography = lazyWithSuspense(
  () => import("#views/Profile/ProfileModules/Demography")
);
const LazyProfileContacts = lazyWithSuspense(
  () => import("#views/Profile/ProfileModules/Contacts")
);
const LazyProfileDocuments = lazyWithSuspense(
  () => import("#views/Profile/ProfileModules/Documents")
);
const LazyProfileProjects = lazyWithSuspense(
  () => import("#views/Profile/ProfileModules/Projects")
);
const LazyProfileNepDat = lazyWithSuspense(
  () => import("#views/Profile/ProfileModules/NepDatProfile")
);
const LazyRiskInfo = lazyWithSuspense(loadRiskInfo);
const LazyDataArchive = lazyWithSuspense(loadDataArchive);
const LazyVizRisk = lazyWithSuspense(loadVizRisk);
const LazyIbf = lazyWithSuspense(loadIbf);
const LazyResponse = lazyWithSuspense(() => import("#views/Response"));
const LazyPalikaReport = lazyWithSuspense(() => import("#views/PalikaReport"));
const LazyFourZeroThree = lazyWithSuspense(
  () => import("#views/FourHundredThree")
);
const LazyFourZeroFour = lazy(() => import("#views/FourHundredFour"));
const LazyForgotPassword = lazyWithSuspense(
  () => import("#views/ForgotPassword")
);

/* admin views */
const LazyAdminLanding = lazyWithSuspense(() => import("#adminViews/Landing"));
const LazyAdminAdmin = lazyWithSuspense(() => import("#adminViews/Admin"));
const LazyAdminBulletin = lazyWithSuspense(
  () => import("#adminViews/Bulletin")
);
const LazyAdminBulletinTable = lazyWithSuspense(
  () => import("#adminViews/BulletinTable")
);
const LazyAdminCovid = lazyWithSuspense(() => import("#adminViews/Covid19"));
const LazyAdminCovidTable = lazyWithSuspense(
  () => import("#adminViews/Covid19Table")
);
const LazyAdminCovidBulk = lazyWithSuspense(
  () => import("#adminViews/Covid19BulkUpload")
);
const LazyAdminEpidemics = lazyWithSuspense(
  () => import("#adminViews/Epidemics")
);
const LazyAdminEpidemicsTable = lazyWithSuspense(
  () => import("#adminViews/EpidemicsTable")
);
const LazyAdminEpidemicsBulk = lazyWithSuspense(
  () => import("#adminViews/EpidemicBulkUpload")
);
const LazyAdminIncidentDatatable = lazyWithSuspense(
  () => import("#adminViews/IncidentDatatable")
);
const LazyAdminAddIncident = lazyWithSuspense(
  () => import("#adminViews/Incidents")
);
const LazyAdminIncidentBulk = lazyWithSuspense(
  () => import("#adminViews/IncidentBulkUpload")
);
const LazyAdminTempShelterTable = lazyWithSuspense(
  () => import("#adminViews/TemporaryShelterTable")
);
const LazyAdminTempShelterDashboard = lazyWithSuspense(
  () => import("#adminViews/TemporaryShelterDashboard")
);
const LazyAdminTempShelter = lazyWithSuspense(
  () => import("#adminViews/TemporaryShelter")
);
const LazyAdminTempShelterPreview = lazyWithSuspense(
  () => import("#adminViews/TemporaryShelterPreview")
);
const LazyAdminTrancheCondition = lazyWithSuspense(
  () => import("#adminViews/TrancheCondition")
);
const LazyAdminTranche1 = lazyWithSuspense(
  () => import("#adminViews/Tranche1")
);
const LazyAdminTranche2 = lazyWithSuspense(
  () => import("#adminViews/Tranche2")
);
const LazyAdminTranche2File = lazyWithSuspense(
  () => import("#adminViews/Tranche2FileUpload")
);
const LazyAdminHealthStr = lazyWithSuspense(
  () => import("#adminViews/HealthDataStr")
);
const LazyAdminHealthTable = lazyWithSuspense(
  () => import("#adminViews/HealthDataTable")
);
const LazyAdminHealthBulk = lazyWithSuspense(
  () => import("#adminViews/HealthBulkUpload")
);
// geohazard - all routes use the same Geohazard component (handles internal routing)
const LazyAdminGeoHazard = lazyWithSuspense(
  () => import("#adminViews/Geohazard")
);
const LazyAdminGeoHazardAssessment = lazyWithSuspense(
  () => import("#adminViews/Geohazard")
);
const LazyAdminGeoHazardFormPreview = lazyWithSuspense(
  () => import("#adminViews/Geohazard")
);

const LazyAdminOverview = lazyWithSuspense(
  () => import("#adminViews/Overview")
);

/* walkthrough slides */
const LazyAbout = lazyWithSuspense(
  () => import("#components/WalkthroughModal/Slide1/About")
);
const LazyDevelopers = lazyWithSuspense(
  () => import("#components/WalkthroughModal/Slide1/Developer")
);
const LazyManuals = lazyWithSuspense(
  () => import("#components/WalkthroughModal/Slide1/Manuals")
);
const LazyFaqs = lazyWithSuspense(
  () => import("#components/WalkthroughModal/Slide1/Faqs")
);

// Helper for non-lazy error pages
const NotFoundPage = () => (
  <Suspense fallback={null}>
    <LazyFourZeroFour />
  </Suspense>
);

/* --------------------------------------------------------------------- */
/*  ROUTES                                                               */
/* --------------------------------------------------------------------- */
export const ROUTES: AppRoute[] = [
  /* ────── PUBLIC ────── */
  {
    path: NAVIGATION_ROUTES.DASHBOARD,
    element: createRouteComponent(LazyDashboard),
    meta: {
      name: "dashboard",
      title: "Dashboard",
      titleNep: "ड्यासबोर्ड",
      navbar: true,
      iconName: "dashboard",
      id: "navbar-dashboard",
    },
  },
  {
    path: NAVIGATION_ROUTES.INCIDENTS.BASE,
    element: createRouteComponent(LazyIncidents),
    meta: {
      name: "incident",
      title: "Incident",
      titleNep: "घटना",
      navbar: true,
      iconName: "incidents",
      id: "navbar-incident",
    },
  },
  {
    path: NAVIGATION_ROUTES.INCIDENTS.REPORT,
    element: createRouteComponent(LazyResponse),
    meta: {
      name: "response",
      title: "Incident Response",
      titleNep: "घटना रिपोर्ट",
      navbar: false,
      id: "navbar-response",
    },
  },
  {
    path: NAVIGATION_ROUTES.DAMAGE_AND_LOSS.BASE,
    element: createRouteComponent(LazyLossAndDamage),
    meta: {
      name: "lossAndDamage",
      title: "Damage & Loss",
      titleNep: "क्षति र नोक्सान",
      navbar: true,
      iconName: "lossAndDamange",
      id: "navbar-lossDamage",
    },
  },
  {
    path: NAVIGATION_ROUTES.REAL_TIME.BASE,
    element: createRouteComponent(LazyRealTime),
    meta: {
      name: "realtime",
      title: "Realtime",
      titleNep: "वास्तविक समय",
      navbar: true,
      iconName: "realtime",
      id: "navbar-realtime",
    },
  },
  {
    path: NAVIGATION_ROUTES.PROFILE.BASE,
    element: createRouteComponent(LazyProfile),
    meta: {
      name: "profile",
      title: "Profile",
      titleNep: "प्रोफाइल",
      navbar: true,
      iconName: "profile",
      id: "navbar-profile",
    },
  },
  {
    path: NAVIGATION_ROUTES.PROFILE.DEMOGRAPHY,
    element: createRouteComponent(LazyProfileDemography),
    meta: {
      name: "demography",
      title: "Demography",
      titleNep: "जनसांख्यिकी",
      navbar: false,
      id: "demography",
    },
  },
  {
    path: NAVIGATION_ROUTES.PROFILE.CONTACTS,
    element: createRouteComponent(LazyProfileContacts),
    meta: {
      name: "contacts",
      title: "Contact",
      titleNep: "सम्पर्क",
      navbar: false,
      id: "contacts",
    },
  },
  {
    path: NAVIGATION_ROUTES.PROFILE.DOCUMENTS,
    element: createRouteComponent(LazyProfileDocuments),
    meta: {
      name: "documents",
      title: "Documents",
      titleNep: "कागजातहरू",
      navbar: false,
      id: "documents",
    },
  },
  {
    path: NAVIGATION_ROUTES.PROFILE.PROJECTS,
    element: createRouteComponent(LazyProfileProjects),
    meta: {
      name: "projects",
      title: "Projects",
      titleNep: "परियोजनाहरू",
      navbar: false,
      disabled: false,
      id: "projects",
    },
  },
  {
    path: NAVIGATION_ROUTES.PROFILE.NEPDAT_PROFILE,
    element: createRouteComponent(LazyProfileNepDat),
    meta: {
      name: "nepdat-profile",
      title: "NepDat Profile",
      titleNep: "नेपडाट प्रोफाइल",
      navbar: false,
      id: "nep-dat",
    },
  },
  {
    path: NAVIGATION_ROUTES.RISK_INFO.BASE,
    element: createRouteComponent(LazyRiskInfo),
    meta: {
      name: "riskInfo",
      title: "Risk Info",
      titleNep: "जोखिम जानकारी",
      navbar: true,
      disabled: false,
      iconName: "riskInfo",
      id: "navbar-riskinfo",
    },
  },
  {
    path: NAVIGATION_ROUTES.DATA_ARCHIVE.BASE,
    element: createRouteComponent(LazyDataArchive),
    meta: {
      name: "dataArchive",
      title: "Data Archive",
      titleNep: "डाटा संग्रह",
      navbar: true,
      disabled: false,
      iconName: "clipboard",
      id: "navbar-dataarchive",
    },
  },
  {
    path: NAVIGATION_ROUTES.VIS_RISK,
    element: createRouteComponent(LazyVizRisk),
    meta: {
      name: "visrisk",
      title: "VisRisk",
      titleNep: "भिज-रिश्‍क",
      navbar: true,
      disabled: false,
      iconName: "eye",
      id: "navbar-visrisk",
    },
  },
  {
    path: NAVIGATION_ROUTES.IBF.BASE,
    element: createRouteComponent(LazyIbf),
    meta: {
      name: "ibf",
      title: "IBF",
      titleNep: "आइ.बि.यफ",
      navbar: true,
      disabled: false,
      iconName: "cloud",
      id: "navbar-ibf",
    },
  },

  /* ────── ADMIN ────── */
  {
    path: NAVIGATION_ROUTES.ADMIN.BASE,
    element: createRouteComponent(LazyAdminLanding),
    meta: {
      name: "admin",
      title: "Admin",
      titleNep: "प्रशासक",
      navbar: false,
      disableIfNoAuth: true,
      id: "admin",
    },
  },
  {
    path: NAVIGATION_ROUTES.ADMIN.ADMIN,
    element: createRouteComponent(LazyAdminAdmin),
    meta: {
      name: "admin",
      title: "Admin",
      titleNep: "प्रशासक",
      navbar: false,
      disableIfNoAuth: true,
      id: "admin-admin",
    },
  },
  {
    path: NAVIGATION_ROUTES.ADMIN.BULLETIN.ADD_NEW_BULLETIN_NEPALI,
    element: createRouteComponent(LazyAdminBulletin),
    meta: {
      name: "bulletin",
      title: "Bulletin (Nepali)",
      titleNep: "बुलेटिन (नेपाली)",
      navbar: false,
      disableIfNoAuth: true,
      id: "bulletin-ne",
    },
  },
  {
    path: NAVIGATION_ROUTES.ADMIN.BULLETIN.ADD_NEW_BULLETIN_ENGLISH,
    element: createRouteComponent(LazyAdminBulletin),
    meta: {
      name: "bulletin",
      title: "Bulletin (English)",
      titleNep: "बुलेटिन (अंग्रेजी)",
      navbar: false,
      disableIfNoAuth: true,
      id: "bulletin-en",
    },
  },
  {
    path: NAVIGATION_ROUTES.ADMIN.BULLETIN.DATATABLE,
    element: createRouteComponent(LazyAdminBulletinTable),
    meta: {
      name: "bulletin",
      title: "Bulletin",
      titleNep: "बुलेटिन",
      navbar: false,
      disableIfNoAuth: true,
      id: "bulletin-table",
    },
  },
  {
    path: NAVIGATION_ROUTES.ADMIN.BULLETIN.EDIT,
    element: createRouteComponent(LazyAdminBulletin),
    meta: {
      name: "edit bulletin",
      title: "Edit Bulletin",
      titleNep: "बुलेटिन सम्पादन गर्नुहोस्",
      navbar: false,
      disableIfNoAuth: true,
      id: "edit-bulletin",
    },
  },
  {
    path: NAVIGATION_ROUTES.ADMIN.COVID19.ADD,
    element: createRouteComponent(LazyAdminCovid),
    meta: {
      name: "Covid-19",
      title: "Covid-19 Form",
      titleNep: "कोभिड-१९ फारम",
      navbar: false,
      disableIfNoAuth: true,
      id: "covid",
    },
  },
  {
    path: NAVIGATION_ROUTES.ADMIN.COVID19.DATATABLE,
    element: createRouteComponent(LazyAdminCovidTable),
    meta: {
      name: "Covid-19-data-table",
      title: "Covid-19 Data Table",
      titleNep: "कोभिड-१९ डाटा तालिका",
      navbar: false,
      disableIfNoAuth: true,
      id: "covid-table",
    },
  },
  {
    path: NAVIGATION_ROUTES.ADMIN.COVID19.BULK_UPLOAD,
    element: createRouteComponent(LazyAdminCovidBulk),
    meta: {
      name: "Covid-19",
      title: "Covid-19 Bulk Upload",
      titleNep: "कोभिड-१९ बल्क अपलोड",
      navbar: false,
      disableIfNoAuth: true,
      id: "covid-bulk-upload",
    },
  },
  {
    path: NAVIGATION_ROUTES.ADMIN.EPIDEMICS.ADD,
    element: createRouteComponent(LazyAdminEpidemics),
    meta: {
      name: "epidemics",
      title: "Epidemics Form",
      titleNep: "महामारी फारम",
      navbar: false,
      disableIfNoAuth: true,
      id: "epidemics",
    },
  },
  {
    path: NAVIGATION_ROUTES.ADMIN.EPIDEMICS.DATATABLE,
    element: createRouteComponent(LazyAdminEpidemicsTable),
    meta: {
      name: "epidemics-data-table",
      title: "Epidemics Data Table",
      titleNep: "महामारी डाटा तालिका",
      navbar: false,
      disableIfNoAuth: true,
      id: "epidemics-table",
    },
  },
  {
    path: NAVIGATION_ROUTES.ADMIN.EPIDEMICS.BULK_UPLOAD,
    element: createRouteComponent(LazyAdminEpidemicsBulk),
    meta: {
      name: "epidemics-upload",
      title: "Epidemics Bulk Upload",
      titleNep: "महामारी बल्क अपलोड",
      navbar: false,
      disableIfNoAuth: true,
      id: "epidemics-upload",
    },
  },
  {
    path: NAVIGATION_ROUTES.ADMIN.INCIDENT.DATATABLE,
    element: createRouteComponent(LazyAdminIncidentDatatable),
    meta: {
      name: "incident-data-table",
      title: "Incident Data Table",
      titleNep: "Incident Data Table",
      navbar: false,
      disableIfNoAuth: true,
      id: "incidents",
    },
  },
  {
    path: NAVIGATION_ROUTES.ADMIN.INCIDENT.ADD,
    element: createRouteComponent(LazyAdminAddIncident),
    meta: {
      name: "incident",
      title: "Incident Form",
      titleNep: "Incident Form",
      navbar: false,
      disableIfNoAuth: true,
      id: "incidents-datatable",
    },
  },
  {
    path: NAVIGATION_ROUTES.ADMIN.INCIDENT.BULK_UPLOAD,
    element: createRouteComponent(LazyAdminIncidentBulk),
    meta: {
      name: "incident-upload",
      title: "Incident Bulk Upload",
      titleNep: "Incident Bulk Upload",
      navbar: false,
      disableIfNoAuth: true,
      id: "incident-upload",
    },
  },
  {
    path: NAVIGATION_ROUTES.ADMIN.TEMPORARY_SHELTER_ENROLLMENT_FORM.DATATABLE,
    element: createRouteComponent(LazyAdminTempShelterTable),
    meta: {
      name: "temporary-shelter-enrollment-form-data-table",
      title: "Temporary shelter enrollment form Data Table",
      titleNep: "Temporary shelter enrollment form Data Table",
      navbar: false,
      disableIfNoAuth: true,
      id: "temporaryShelter-table-",
    },
  },
  {
    path: NAVIGATION_ROUTES.ADMIN.TEMPORARY_SHELTER_ENROLLMENT_FORM.DASHBOARD,
    element: createRouteComponent(LazyAdminTempShelterDashboard),
    meta: {
      name: "temporary-shelter-enrollment-form-data-table",
      title: "Temporary shelter enrollment form Data Table",
      titleNep: "Temporary shelter enrollment form Data Table",
      navbar: false,
      disableIfNoAuth: true,
      id: "temporaryShelter-table-dashboard",
    },
  },
  {
    path: NAVIGATION_ROUTES.ADMIN.TEMPORARY_SHELTER_ENROLLMENT_FORM.ADD,
    element: createRouteComponent(LazyAdminTempShelter),
    meta: {
      name: "add-new-temporary-shelter-enrollment-data",
      title: "Add New Temporary shelter enrollment data",
      titleNep: "Add New Temporary shelter enrollment data",
      navbar: false,
      disableIfNoAuth: true,
      id: "",
    },
  },
  {
    path: NAVIGATION_ROUTES.ADMIN.TEMPORARY_SHELTER_ENROLLMENT_FORM.DETAILS,
    element: createRouteComponent(LazyAdminTempShelterPreview),
    meta: {
      name: "add-new-temporary-shelter-enrollment-data-preview",
      title: "Add New Temporary shelter enrollment data preview",
      titleNep: "Add New Temporary shelter enrollment data preview",
      disableIfNoAuth: true,
      id: "",
    },
  },
  {
    path: "/admin/temporary-shelter-enrollment-form/add-tranche-condition/:id",
    element: createRouteComponent(LazyAdminTrancheCondition),
    meta: {
      name: "add-new-temporary-shelter-enrollment-data-preview",
      title: "Add New Temporary shelter enrollment data preview",
      titleNep: "Add New Temporary shelter enrollment data preview",
      navbar: false,
      disableIfNoAuth: true,
      id: "",
    },
  },
  {
    path: "/admin/temporary-shelter-enrollment-form/add-view-tranche1/:id",
    element: createRouteComponent(LazyAdminTranche1),
    meta: {
      name: "add-view-tranche1",
      title: "Add View Tranche1",
      titleNep: "Add View Tranche1",
      navbar: false,
      disableIfNoAuth: true,
      id: "",
    },
  },
  {
    path: "/admin/temporary-shelter-enrollment-form/add-view-tranche2/:id",
    element: createRouteComponent(LazyAdminTranche2),
    meta: {
      name: "add-view-tranche2",
      title: "Add View Tranche2",
      titleNep: "Add View Tranche2",
      navbar: false,
      disableIfNoAuth: true,
      id: "",
    },
  },
  {
    path: "/admin/temporary-shelter-enrollment-form/add-tranche2-file-upload/:id",
    element: createRouteComponent(LazyAdminTranche2File),
    meta: {
      name: "add-tranche2-file-upload",
      title: "Add Tranche2 file upload",
      titleNep: "Add Tranche2 File Upload",
      navbar: false,
      disableIfNoAuth: true,
      id: "",
    },
  },
  {
    path: NAVIGATION_ROUTES.ADMIN.HEALTH_INFRASTRUCTURE.ADD,
    element: createRouteComponent(LazyAdminHealthStr),
    meta: {
      name: "healthinfrastructure",
      title: "Health Infrastructure Form",
      titleNep: "स्वास्थ्य पूर्वाधार फारम",
      navbar: false,
      disableIfNoAuth: true,
      id: "health-infra",
    },
  },
  {
    path: NAVIGATION_ROUTES.ADMIN.HEALTH_INFRASTRUCTURE.DATATABLE,
    element: createRouteComponent(LazyAdminHealthTable),
    meta: {
      name: "healthinfrastructure-data-table",
      title: "Health Infrastructure Data Table",
      titleNep: "स्वास्थ्य पूर्वाधार डाटा तालिका",
      navbar: false,
      disableIfNoAuth: true,
      id: "health-infra-table",
    },
  },
  {
    path: NAVIGATION_ROUTES.ADMIN.HEALTH_INFRASTRUCTURE.BULK_UPLOAD,
    element: createRouteComponent(LazyAdminHealthBulk),
    meta: {
      name: "healthinfrastructure-upload",
      title: "Health Infrastructure Bulk Upload",
      titleNep: "स्वास्थ्य पूर्वाधार बल्क अपलोड",
      navbar: false,
      disableIfNoAuth: true,
      id: "health-bulk-upload",
    },
  },
  // geohazard
  {
    path: NAVIGATION_ROUTES.ADMIN.GEO_HAZARD.BASE,
    element: createRouteComponent(LazyAdminGeoHazard),
    meta: {
      name: "geohazard",
      title: "Geo Hazard",
      titleNep: "भू खतरा",
      navbar: false,
      disableIfNoAuth: true,
      id: "geo-hazard",
    },
  },
  {
    path: NAVIGATION_ROUTES.ADMIN.GEO_HAZARD.ASSESSMENT_REQUEST_FORM,
    element: createRouteComponent(LazyAdminGeoHazardAssessment),
    meta: {
      name: "geohazard-assessment",
      title: "Geo Hazard Assessment",
      titleNep: "भू खतरा मूल्यांकन",
      navbar: false,
      disableIfNoAuth: true,
      id: "geo-hazard-assessment",
    },
  },
  {
    path: NAVIGATION_ROUTES.ADMIN.GEO_HAZARD.FORM_PREVIEW,
    element: createRouteComponent(LazyAdminGeoHazardFormPreview),
    meta: {
      name: "geohazard-form-preview",
      title: "Geo Hazard Form Preview",
      titleNep: "भू खतरा फारम पूर्वावलोकन",
      navbar: false,
      disableIfNoAuth: true,
      id: "geo-hazard-form-preview",
    },
  },
  {
    path: NAVIGATION_ROUTES.ADMIN.OVERVIEW,
    element: createRouteComponent(LazyAdminOverview),
    meta: {
      name: "overview",
      title: "Overview",
      titleNep: "सिंहावलोकन",
      navbar: false,
      disableIfNoAuth: true,
      id: "overview",
    },
  },

  /* ────── WALKTHROUGH ────── */
  {
    path: NAVIGATION_ROUTES.ABOUT,
    element: createRouteComponent(LazyAbout),
    meta: {
      name: "about",
      title: "About",
      navbar: false,
      disabled: false,
      iconName: "eye",
      id: "",
    },
  },
  {
    path: NAVIGATION_ROUTES.DEVELOPERS,
    element: createRouteComponent(LazyDevelopers),
    meta: {
      name: "developers",
      title: "Developers",
      navbar: false,
      disabled: false,
      iconName: "eye",
      id: "",
    },
  },
  {
    path: NAVIGATION_ROUTES.MANUALS,
    element: createRouteComponent(LazyManuals),
    meta: {
      name: "manuals",
      title: "Manuals",
      navbar: false,
      disabled: false,
      iconName: "eye",
      id: "",
    },
  },
  {
    path: NAVIGATION_ROUTES.FAQS,
    element: createRouteComponent(LazyFaqs),
    meta: {
      name: "faqs",
      title: "Faqs",
      navbar: false,
      disabled: false,
      iconName: "eye",
      id: "",
    },
  },

  /* ────── MISC ────── */
  {
    path: NAVIGATION_ROUTES.DRRM_REPORT,
    element: createRouteComponent(LazyPalikaReport),
    meta: {
      name: "DRRM Report",
      title: "DRRM Report",
      titleNep: "डिआरआरएम रिपोर्ट",
      navbar: false,
      disabled: false,
      iconName: "textDocument",
      id: "drrm",
    },
  },
  {
    path: NAVIGATION_ROUTES.FOUR_ZERO_THREE,
    element: createRouteComponent(LazyFourZeroThree),
    meta: {
      name: "fourHundredThree",
      title: "403",
      navbar: false,
      id: "navbar-fourHundredThree",
    },
  },
  {
    path: NAVIGATION_ROUTES.SET_NEW_PASSWORD,
    element: createRouteComponent(LazyForgotPassword),
    meta: {
      name: "forgotPassword",
      title: "New Password",
      navbar: false,
      id: "navbar-forgetpassword",
    },
  },
  {
    path: "*",
    element: createRouteComponent(NotFoundPage),
    meta: {
      name: "fourHundredFour",
      title: "404 - Page Not Found",
      titleNep: "४०४ - पृष्ठ फेला परेन",
      navbar: false,
      id: "not-found",
    },
  },
];
